<template>
    <div class="application-rule">
        <app-select
            :show-platform-group-options="false"
            :preselect-first="false"
            :read-only="readOnly"
            :apps="resources"
            :rule="rule"
            @input="$emit('input', $event)" />
        <template v-if="appId">
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Condition') }}</strong>
                <div class="flex-break" />
            </template>
            <fact-select
                :options="factOptions"
                :value="factValue"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Criteria') }}</strong>
                <div class="flex-break" />
            </template>
            <simple-select
                :options="usedOptions"
                :value="usedValue"
                preselect-first
                field="used"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <component
                :is="valueComponent.component"
                v-if="valueComponent"
                v-bind="valueComponent.props"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
        </template>
    </div>
</template>

<script>
import AppSelect from '@/segments/components/segment-editor-vue/components/app-select.vue';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import {
    getFactValue,
    getFactOptions,
    formatFactOptions,
    getRelativeDateRangeConfig,
    getAbsoluteDateRangeConfig,
    getSingleDateConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';

export default {
    name: 'ApplicationRule',
    components: {
        FactSelect,
        ResourceSelect,
        SimpleSelect,
        SingleDate,
        NumberInput,
        AbsoluteDateRange,
        RelativeDateRange,
        AppSelect,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    computed: {
        appId () {
            return this.resources.find((app) => app.id === this.rule.appId);
        },
        factOptions () {
            const options = getFactOptions(this.rule, this.config.onlyAllowGuideCompatibleRules);

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        usedOptions () {
            const options = [
                { used: 'ever', name: this.$t('applicationRule.ever') },
                { used: 'before', name: this.$t('applicationRule.before') },
                { used: 'since', name: this.$t('applicationRule.since') },
                { used: 'withinLast', name: this.$t('applicationRule.within last') },
            ];

            if (this.rule.fact === 'lastused') {
                return [
                    { used: 'before', name: this.$t('applicationRule.before') },
                    { used: 'since', name: this.$t('applicationRule.since') },
                    { used: 'withinLast', name: this.$t('applicationRule.within last') },
                    { used: '!withinLast', name: this.$t('applicationRule.not within last') },
                    { used: 'between', name: this.$t('applicationRule.between') },
                ];
            }

            return options;
        },
        usedValue () {
            return this.usedOptions.find((option) => option.used === this.rule.used);
        },
        valueComponent () {
            switch (this.rule.used) {
                case 'withinLast':
                case '!withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case 'since':
                case 'before':
                    return getSingleDateConfig(this.rule, 'value');
                case 'between':
                    return getAbsoluteDateRangeConfig(this.rule);
                default:
                    return false;
            }
        },
    },
};
</script>
<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
